/**
 * Firebase Cloud Messaging (web push) helper.
 *
 * Browsers now receive push via Firebase (an FCM web registration token), not raw VAPID
 * Web Push. The public Firebase config comes from env (VITE_FIREBASE_*) and points at the
 * one shared Firebase project; the backend sends to the returned token via FCM HTTP v1.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, type Messaging, type MessagePayload } from 'firebase/messaging';
import { ENV } from '@/config/environment';

export const firebaseConfig = {
  apiKey: ENV.FIREBASE_API_KEY,
  authDomain: ENV.FIREBASE_AUTH_DOMAIN,
  projectId: ENV.FIREBASE_PROJECT_ID,
  messagingSenderId: ENV.FIREBASE_MESSAGING_SENDER_ID,
  appId: ENV.FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

/** True only when all required public Firebase config (incl. the web VAPID key) is present. */
export function isFirebaseConfigured(): boolean {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId &&
    ENV.FIREBASE_VAPID_KEY
  );
}

/** Builds the firebase-messaging-sw.js URL with the config as query params (SWs can't read env). */
export function firebaseSwUrl(): string {
  const params = new URLSearchParams();
  if (firebaseConfig.apiKey) params.set('apiKey', firebaseConfig.apiKey);
  if (firebaseConfig.authDomain) params.set('authDomain', firebaseConfig.authDomain);
  if (firebaseConfig.projectId) params.set('projectId', firebaseConfig.projectId);
  if (firebaseConfig.messagingSenderId) params.set('messagingSenderId', firebaseConfig.messagingSenderId);
  if (firebaseConfig.appId) params.set('appId', firebaseConfig.appId);
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

async function getMessagingInstance(): Promise<Messaging | null> {
  if (!isFirebaseConfigured()) return null;
  try {
    if (!(await isSupported())) return null;
  } catch {
    return null;
  }
  if (!app) app = initializeApp(firebaseConfig);
  if (!messaging) messaging = getMessaging(app);
  return messaging;
}

/**
 * Obtains an FCM web registration token, using the firebase-messaging service worker.
 * Returns null when Firebase isn't configured / supported or permission isn't granted.
 */
export async function getFcmWebToken(swRegistration: ServiceWorkerRegistration): Promise<string | null> {
  const m = await getMessagingInstance();
  if (!m) return null;
  try {
    return await getToken(m, {
      vapidKey: ENV.FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });
  } catch (error) {
    console.error('[Push] getToken failed:', error);
    return null;
  }
}

/** Registers a foreground-message handler (notifications received while the tab is focused). */
export async function onForegroundMessage(cb: (payload: MessagePayload) => void): Promise<void> {
  const m = await getMessagingInstance();
  if (!m) return;
  onMessage(m, cb);
}
